import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import {
  ImportOptions,
  ImportResult,
  MatchedSheet,
  SheetImporter,
  SheetParseResult,
} from '../schemas/sheet-importer.interface';
import { CsvSeparator } from '../../../shared/services/file-export/file-export.service';
import { TimeSeriesTablesService } from '../../../features/timeseries-data/timeseries-tables.service';
import {
  TimeSeriesImportService,
  TimeSeriesParseResult,
} from '../../../features/timeseries-data/services/timeseries-import.service';
import { TimeSeriesTable } from '../../../features/timeseries-data/models/timeseries-table.model';
import { TimeSeriesDataPointsService } from '../../../features/timeseries-data/services/timeseries-data-points.service';

const BATCH_SIZE = 250;

function extractHttpErrorMessage(err: unknown): string {
  if (err instanceof HttpErrorResponse) {
    const body = err.error as { message?: string | string[] } | null;
    if (body && typeof body === 'object') {
      if (Array.isArray(body.message)) return body.message.join('; ');
      if (typeof body.message === 'string') return body.message;
    }
  }
  return err instanceof Error ? err.message : 'Import failed';
}

@Injectable({ providedIn: 'root' })
export class TimeseriesSheetImporterService implements SheetImporter {
  private readonly timeseriesTableService = inject(TimeSeriesTablesService);
  private readonly timeseriesImportService = inject(TimeSeriesImportService);
  private readonly timeseriesDataPointsService = inject(TimeSeriesDataPointsService);

  matchSheet(sheetName: string, sheetIndex: number): MatchedSheet | null {
    const matchedTable = this.timeseriesTableService
      .tables()
      .find((table) => table.name.toLowerCase().trim() === sheetName.toLowerCase().trim());

    if (!matchedTable) return null;

    return {
      sheetName,
      sheetIndex,
      sectionLabel: matchedTable.name,
      meta: matchedTable,
    };
  }

  async parseSheet(
    file: File,
    match: MatchedSheet,
    separator: CsvSeparator,
  ): Promise<SheetParseResult> {
    const table = match.meta as TimeSeriesTable;
    const result = await this.timeseriesImportService.parseFile(
      file,
      table,
      separator,
      { sheetIndex: match.sheetIndex },
    );

    return {
      validRowCount: result.points.length,
      errors: result.errors.map((e) => ({ row: e.row, message: e.messageKey })),
      parsed: result,
    };
  }

  async importRecords(
    match: MatchedSheet,
    parseResult: SheetParseResult,
    options: ImportOptions,
  ): Promise<ImportResult> {
    const table = match.meta as TimeSeriesTable;
    const { points } = parseResult.parsed as TimeSeriesParseResult;
    const totals: ImportResult = { created: 0, updated: 0, errors: [] };

    for (let i = 0; i < points.length; i += BATCH_SIZE) {
      const batch = points.slice(i, i + BATCH_SIZE);
      try {
        const inserted = await firstValueFrom(
          this.timeseriesDataPointsService.bulkCreate(table.id, {
            points: batch,
            duplicateHandlingOverride: options.duplicateHandling,
          }),
        );
        totals.created += inserted.length;
      } catch (err) {
        totals.errors.push({ row: i + 1, message: extractHttpErrorMessage(err) });
      }
    }

    return totals;
  }
}
